import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { MemberIndexEntry, MemberRole, MinistryMeta } from '../types/types';
import Layout from '../components/Layout';
import RoleTag from '../components/RoleTag';
import { useBreakpoint } from '../useBreakpoint';
import { shortPartyName } from '../utils/party';
import { photoFor } from '../utils/names';
import { getAllMembers, getGovernmentBills, getMinistriesDoc, getParties, GovBill, GovBillStats } from '../data/cache';
import { color, font, radius, shadow, space, text, weight } from '../theme';
import PageHeader from '../components/PageHeader';
import TabBar from '../components/TabBar';
import { Card, Section } from '../components/Card';
import { StatTile, StatTileRow } from '../components/StatTile';
import { Badge, StageBadge } from '../components/Badge';
import Chip, { ChipRow } from '../components/Chip';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import SearchField from '../components/SearchField';
import Button from '../components/Button';
import { EmptyState, ErrorState } from '../components/States';
import { SkeletonList, SkeletonTiles } from '../components/Skeleton';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import PresenceLegend from '../components/PresenceLegend';
import { usePresenceNow } from '../hooks/usePresence';
import { swornInLabel, useScope } from '../hooks/useScope';
import Notice from '../components/Notice';
import { isPresenceStale } from '../utils/presence';

/* ── Config ─────────────────────────────────────────────────────────────── */

type GovTab = 'ministers' | 'ministries' | 'bills';
const GOV_TABS: GovTab[] = ['ministers', 'ministries', 'bills'];
type BillFilter = 'all' | 'passed' | 'inProgress' | 'stopped';
const BILLS_PAGE = 20;

const PASSED = ['עברה'];
const STOPPED = ['נעצרה', 'מוזגה', 'הוסבה'];
const IN_PROGRESS = ['דיון מוקדם', 'בוועדה', 'קריאה ראשונה', 'קריאה שנייה ושלישית', 'קריאה שלישית'];

interface CabinetMember {
  member: MemberIndexEntry;
  govRoles: MemberRole[];
  primaryRole: string;
  partyName: string;
  isActiveMk: boolean;
  hierarchy: number; // 1 PM, 2 deputy PM, 5 minister, 8 deputy minister
}

interface MinistryEntry extends MinistryMeta {
  id: string;
}

const hierarchyFromTitle = (title: string): number => {
  if (title === 'ראש הממשלה') return 1;
  if (title.includes('סגן ראש הממשלה') || title.includes('המשנה לראש הממשלה')) return 2;
  if (title.includes('סגן שר') || title.includes('סגנית שר')) return 8;
  return 5;
};

// "משרד הבריאות" → "הבריאות"
const trimMinistry = (name: string) => name.replace('משרד ', '').replace('המשרד ל', '').replace('המשרד ה', '');

const formatShortDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

/* ── Minister row ───────────────────────────────────────────────────────── */

const MinisterRow = ({ gov, ministries, first, present }: { gov: CabinetMember; ministries: { name: string; count: number }[]; first: boolean; present: boolean }) => (
  <ListRow
    divider={!first}
    to={`/members/${gov.member.id}`}
    state={{ from: 'הממשלה' }}
    leading={<Avatar name={gov.member.name} src={photoFor(gov.member, 44)} size={44} present={present} />}
    title={
      <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
        {gov.member.name}
        {!gov.isActiveMk && <span title="ויתר/ה על המושב בכנסת לפי החוק הנורווגי"><Badge>נורווגי</Badge></span>}
      </span>
    }
    subtitle={
      <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs }}>
          {gov.govRoles.map((r, i) => <RoleTag key={i} role={r} size="small" />)}
        </span>
        <span>
          {gov.partyName}
          {ministries.filter(m => m.count > 0).map(m => ` · ${m.count} שאילתות ל${trimMinistry(m.name)}`).join('')}
        </span>
      </span>
    }
  />
);

/* ── Page ───────────────────────────────────────────────────────────────── */

const GovernmentPage = () => {
  const { isTablet, isDesktop } = useBreakpoint();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cabinet, setCabinet] = useState<CabinetMember[] | null>(null);
  const [ministries, setMinistries] = useState<MinistryEntry[]>([]);
  const [govBillStats, setGovBillStats] = useState<GovBillStats | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const scope = useScope();
  const [govBills, setGovBills] = useState<GovBill[] | null>(null);
  const [billsError, setBillsError] = useState(false);
  // Who is in the Knesset building now: dots on the photos, while the data is fresh
  const presence = usePresenceNow();
  const presentNow: Record<string, string> = presence.now && !isPresenceStale(presence.now, presence.clock) ? presence.now.present : {};

  // State lives in the URL so Back restores it; older links may still pass the tab in history state
  const stateTab = (location.state as { tab?: string } | null)?.tab;
  const urlTab = searchParams.get('tab') || stateTab || 'ministers';
  const activeTab: GovTab = (GOV_TABS as string[]).includes(urlTab) ? (urlTab as GovTab) : 'ministers';
  const miniSort = searchParams.get('sort') === 'queries' ? 'queries' : 'name';
  const billFilter = (searchParams.get('bf') as BillFilter) || 'all';
  const billSearch = searchParams.get('bq') || '';
  const billsShown = parseInt(searchParams.get('bn') || '', 10) || BILLS_PAGE;

  const updateParams = useCallback((changes: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const keys = Object.keys(changes);
      for (let i = 0; i < keys.length; i++) {
        const v = changes[keys[i]];
        if (v === null || v === '') next.delete(keys[i]); else next.set(keys[i], v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Cabinet from the member index (roles with category 'government'), ministries from meta
  useEffect(() => {
    let cancelled = false;
    setError(false);
    Promise.all([getAllMembers(), getParties(), getMinistriesDoc()]).then(([members, parties, mdoc]) => {
      if (cancelled) return;
      const partyNames: Record<string, string> = {};
      for (const p of parties) partyNames[p.id] = shortPartyName(p.name || '');

      const list: CabinetMember[] = [];
      for (const m of members) {
        const govRoles = (m.roles || []).filter(r => r.category === 'government');
        if (govRoles.length === 0) continue;
        const sorted = govRoles.slice().sort((a, b) => hierarchyFromTitle(a.title) - hierarchyFromTitle(b.title));
        list.push({
          member: m,
          govRoles,
          primaryRole: sorted[0].title,
          partyName: m.partyId ? partyNames[m.partyId] || '' : '',
          isActiveMk: m.isActiveMk !== false,
          hierarchy: hierarchyFromTitle(sorted[0].title),
        });
      }
      // Senior first, then more portfolios first, then by name
      list.sort((a, b) =>
        a.hierarchy - b.hierarchy ||
        b.govRoles.length - a.govRoles.length ||
        a.member.name.localeCompare(b.member.name, 'he'));
      setCabinet(list);

      const entries: MinistryEntry[] = [];
      const ids = Object.keys(mdoc.byId);
      for (let i = 0; i < ids.length; i++) {
        const m = mdoc.byId[ids[i]];
        // Skip inactive ministries (בלי תיק, ההסברה, המודיעין, …)
        if (m.isActive === false) continue;
        entries.push({ ...m, id: ids[i], category: m.CategoryName || m.categoryName || m.category || '' });
      }
      setMinistries(entries);
      setGovBillStats(mdoc.govBillStats);
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Government bills load only when their tab is opened
  useEffect(() => {
    if (activeTab !== 'bills' || govBills) return;
    let cancelled = false;
    setBillsError(false);
    getGovernmentBills()
      .then(list => { if (!cancelled) setGovBills(list); })
      .catch(() => { if (!cancelled) setBillsError(true); });
    return () => { cancelled = true; };
  }, [activeTab, govBills, reloadKey]);

  // minister → the ministries they head (with query counts), for the minister rows
  const ministriesByMinister = useMemo(() => {
    const grouped: Record<string, { name: string; count: number }[]> = {};
    for (const m of ministries) {
      if (!m.currentMinisterId) continue;
      if (!grouped[m.currentMinisterId]) grouped[m.currentMinisterId] = [];
      grouped[m.currentMinisterId].push({ name: m.name, count: m.queryCount || 0 });
    }
    return grouped;
  }, [ministries]);

  const cabinetById = useMemo(() => {
    const map: Record<string, CabinetMember> = {};
    for (const cm of cabinet || []) map[cm.member.id] = cm;
    return map;
  }, [cabinet]);

  const sortedMinistries = useMemo(() => ministries.slice().sort((a, b) =>
    miniSort === 'queries' ? (b.queryCount || 0) - (a.queryCount || 0) : a.name.localeCompare(b.name, 'he')), [ministries, miniSort]);

  const filteredBills = useMemo(() => {
    let list = govBills || [];
    if (billFilter === 'passed') list = list.filter(b => PASSED.includes(b.simpleStage || ''));
    else if (billFilter === 'inProgress') list = list.filter(b => IN_PROGRESS.includes(b.simpleStage || ''));
    else if (billFilter === 'stopped') list = list.filter(b => STOPPED.includes(b.simpleStage || ''));
    const term = billSearch.trim();
    if (term) list = list.filter(b => b.title.includes(term));
    return list;
  }, [govBills, billFilter, billSearch]);

  const pm = cabinet?.find(m => m.hierarchy === 1);
  const groups: { title: string; members: CabinetMember[] }[] = cabinet ? [
    { title: 'סגני ראש הממשלה', members: cabinet.filter(m => m.hierarchy === 2) },
    { title: 'שרים', members: cabinet.filter(m => m.hierarchy === 5) },
    { title: 'סגני שרים', members: cabinet.filter(m => m.hierarchy === 8) },
  ] : [];
  const ministersPresent = cabinet ? cabinet.filter(m => !!presentNow[m.member.id]).length : 0;

  return (
    <Layout width="full">
      <PageContainer>
      <div style={{ padding: isDesktop ? 0 : `${space.sm}px ${space.lg}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        <PageHeader
          title="הממשלה"
          subtitle={cabinet ? `הממשלה ה-${scope.government} · ${cabinet.length} שרים וסגני שרים · ${ministries.length} משרדים` : `הממשלה ה-${scope.government}`}
        />
        {scope.governmentTransitional && (
          <Notice title="ממשלת מעבר">
            הכנסת ה-{scope.knesset} הושבעה{swornInLabel(scope) ? ` ב-${swornInLabel(scope)}` : ''}. הממשלה ה-{scope.government} ממשיכה לכהן עד שתושבע ממשלה חדשה, והשרים נשארים בתפקידם.
          </Notice>
        )}

        <TabBar
          label="תצוגה"
          tabs={[
            { key: 'ministers', label: 'שרים', count: cabinet ? cabinet.length : undefined },
            { key: 'ministries', label: 'משרדים', count: cabinet ? ministries.length : undefined },
            { key: 'bills', label: 'חקיקה' },
          ]}
          active={activeTab}
          onChange={t => updateParams({ tab: t === 'ministers' ? null : t })}
          style={isDesktop ? { maxWidth: 480 } : undefined}
        />

        {error && <Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card>}
        {!error && !cabinet && (
          <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
            <SkeletonTiles count={3} />
            <SkeletonList rows={6} avatar />
          </div>
        )}

        {/* ═══ MINISTERS TAB ═══ (desktop: the note and the PM card in a sticky column beside the groups) */}
        {cabinet && activeTab === 'ministers' && (
          <TwoPane sideWidth={340} gap={space.xxl} side={
          <div style={isDesktop ? { display: 'flex', flexDirection: 'column', gap: space.lg } : { display: 'contents' }}>
            <Card style={{ background: color.primarySoft, boxShadow: 'none' }}>
              <div style={{ fontSize: font.small, fontWeight: weight.bold, color: color.primaryInk, marginBottom: space.xs }}>הפרדת רשויות</div>
              <p style={{ margin: 0, fontSize: font.label, lineHeight: 1.65, color: color.primaryInk }}>
                הממשלה (הרשות המבצעת) מוקמת על ידי הקואליציה ואחראית על הפעלת המשרדים וביצוע המדיניות.
                הכנסת (הרשות המחוקקת) מורכבת מ-120 חברי כנסת שמחוקקים חוקים ומפקחים על הממשלה.
              </p>
              <p style={{ margin: `${space.sm}px 0 0`, fontSize: font.small, color: color.primaryInk }}>
                חלק מהשרים ויתרו על מושבם בכנסת לפי &quot;החוק הנורווגי&quot;.
              </p>
            </Card>

            {pm && (
              <Link to={`/members/${pm.member.id}`} state={{ from: 'הממשלה' }} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: space.xs,
                padding: space.xl, background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card,
                color: color.text, textDecoration: 'none',
              }}>
                <span style={{ fontSize: font.caption, fontWeight: weight.bold, color: color.primaryInk }}>ראש הממשלה</span>
                <span style={{ margin: `${space.xs}px 0` }}><Avatar name={pm.member.name} src={photoFor(pm.member, 88)} size={88} present={!!presentNow[pm.member.id]} /></span>
                <span style={text.entityTitle}>{pm.member.name}</span>
                <span style={{ fontSize: font.small, color: color.textSecondary }}>{pm.partyName}</span>
                {pm.govRoles.length > 1 && (
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs, justifyContent: 'center', marginTop: space.xs }}>
                    {pm.govRoles.filter(r => r.title !== 'ראש הממשלה').map((r, i) => <RoleTag key={i} role={r} size="small" />)}
                  </span>
                )}
              </Link>
            )}
          </div>
          }>
          <div style={isDesktop ? { display: 'flex', flexDirection: 'column', gap: space.lg } : { display: 'contents' }}>

            {ministersPresent > 0 && (
              <PresenceLegend count={ministersPresent} noun="מחברי הממשלה" to="/parties?tab=presence&pFilter=ministers" state={{ from: 'הממשלה' }} />
            )}

            {groups.filter(g => g.members.length > 0).map(g => (
              <Section key={g.title} title={g.title} meta={g.members.length}>
                <Card padding={0}>
                  {g.members.map((m, i) => <MinisterRow key={m.member.id} gov={m} ministries={ministriesByMinister[m.member.id] || []} first={i === 0} present={!!presentNow[m.member.id]} />)}
                </Card>
              </Section>
            ))}

            {cabinet.length === 0 && <Card padding={0}><EmptyState icon="users" title="אין נתוני ממשלה" /></Card>}
          </div>
          </TwoPane>
        )}

        {/* ═══ MINISTRIES TAB ═══ */}
        {cabinet && activeTab === 'ministries' && (
          <>
            <StatTileRow columns={3}>
              <StatTile value={ministries.length} label="משרדים" />
              <StatTile value={ministries.filter(m => m.currentMinisterId).length} label="עם שר/ה" />
              <StatTile value={ministries.reduce((s, m) => s + (m.queryCount || 0), 0).toLocaleString('he-IL')} label="שאילתות" />
            </StatTileRow>

            <ChipRow style={{ alignItems: 'center' }}>
              <span style={{ fontSize: font.small, color: color.textSecondary, flexShrink: 0 }}>מיון:</span>
              <Chip selected={miniSort === 'name'} onClick={() => updateParams({ sort: null })}>א-ת</Chip>
              <Chip selected={miniSort === 'queries'} onClick={() => updateParams({ sort: 'queries' })}>שאילתות</Chip>
            </ChipRow>

            {ministries.length === 0
              ? <Card padding={0}><EmptyState icon="document" title="אין נתוני משרדים" /></Card>
              : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isDesktop
                    ? 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))'
                    : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
                  gap: space.md,
                }}>
                  {sortedMinistries.map(ministry => {
                    const cm = ministry.currentMinisterId ? cabinetById[ministry.currentMinisterId] : undefined;
                    return (
                      <Link key={ministry.id} to={`/government/ministries/${ministry.id}`} state={{ from: 'הממשלה' }} style={{
                        display: 'flex', flexDirection: 'column', gap: space.sm, padding: space.lg,
                        background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card, color: color.text, textDecoration: 'none',
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
                          {ministry.category && <Badge>{ministry.category}</Badge>}
                          {(ministry.queryCount || 0) > 0 && <span style={{ fontSize: font.caption, color: color.textSecondary, marginInlineStart: 'auto' }}>{ministry.queryCount} שאילתות</span>}
                        </span>
                        <span style={{ fontSize: font.body + 1, fontWeight: weight.medium, lineHeight: 1.35 }}>{ministry.name}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, paddingTop: space.sm, borderTop: `1px solid ${color.divider}` }}>
                          {cm
                            ? <>
                                <Avatar name={cm.member.name} src={photoFor(cm.member, 32)} size={32} />
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ display: 'block', fontSize: font.label, fontWeight: weight.medium }}>{cm.member.name}</span>
                                  <span style={{ display: 'block', fontSize: font.caption, color: color.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cm.primaryRole}</span>
                                </span>
                              </>
                            : <span style={{ fontSize: font.small, color: color.textTertiary }}>אין שר/ה ממונה</span>}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
          </>
        )}

        {/* ═══ GOVERNMENT BILLS TAB ═══ (desktop: tiles and search in a sticky column beside the list) */}
        {cabinet && activeTab === 'bills' && (
          <TwoPane sideWidth={340} gap={space.xxl} side={
          <div style={isDesktop ? { display: 'flex', flexDirection: 'column', gap: space.lg } : { display: 'contents' }}>
            {govBillStats && (
              <StatTileRow columns={isDesktop ? 2 : 4}>
                {([
                  { key: 'all' as BillFilter, value: govBillStats.total, label: 'סה״כ' },
                  { key: 'passed' as BillFilter, value: govBillStats.passed, label: 'עברו' },
                  { key: 'inProgress' as BillFilter, value: govBillStats.inProgress, label: 'בתהליך' },
                  { key: 'stopped' as BillFilter, value: govBillStats.total - govBillStats.passed - govBillStats.inProgress, label: 'נעצרו' },
                ]).map(s => (
                  <StatTile
                    key={s.key}
                    value={s.value}
                    label={s.label}
                    selected={billFilter === s.key}
                    indicator="filter"
                    onClick={() => updateParams({ bf: billFilter === s.key || s.key === 'all' ? null : s.key, bn: null })}
                  />
                ))}
              </StatTileRow>
            )}

            <SearchField value={billSearch} onChange={v => updateParams({ bq: v, bn: null })} placeholder="חיפוש הצעת חוק ממשלתית" />
          </div>
          }>
          <div style={isDesktop ? { display: 'flex', flexDirection: 'column', gap: space.lg } : { display: 'contents' }}>

            {(billFilter !== 'all' || billSearch.trim()) && govBills && (
              <div style={{ fontSize: font.small, color: color.textSecondary }}>{filteredBills.length} תוצאות</div>
            )}

            {billsError && <Card padding={0}><ErrorState onRetry={() => { setBillsError(false); setGovBills(null); setReloadKey(k => k + 1); }} /></Card>}
            {!billsError && !govBills && <SkeletonList rows={6} />}
            {govBills && filteredBills.length === 0 && (
              <Card padding={0}>
                <EmptyState
                  title={billSearch.trim() ? 'לא נמצאו תוצאות' : 'אין הצעות חוק ממשלתיות'}
                  action={(billFilter !== 'all' || billSearch.trim()) ? <Button variant="ghost" onClick={() => updateParams({ bf: null, bq: null, bn: null })}>ניקוי הסינון</Button> : undefined}
                />
              </Card>
            )}
            {govBills && filteredBills.length > 0 && (
              <Card padding={0}>
                {filteredBills.slice(0, billsShown).map((b, i) => (
                  <ListRow
                    key={b.id}
                    divider={i > 0}
                    to={`/legislation/${b.id}`}
                    state={{ from: 'הממשלה' }}
                    title={b.title}
                    subtitle={<span style={{ display: 'flex', alignItems: 'center', gap: space.sm, marginTop: space.xxs }}><StageBadge stage={b.simpleStage} />{formatShortDate(b.lastUpdated)}</span>}
                  />
                ))}
              </Card>
            )}
            {govBills && billsShown < filteredBills.length && (
              <Button fullWidth onClick={() => updateParams({ bn: String(billsShown + BILLS_PAGE) })}>
                הצגת עוד ({filteredBills.length - billsShown} נותרו)
              </Button>
            )}
          </div>
          </TwoPane>
        )}

      </div>
      </PageContainer>
    </Layout>
  );
};

export default GovernmentPage;
